// Desktop → cloud sync contract. The API validates pushes with these schemas;
// the desktop app (Go, desktop/internal/cloud) mirrors the shapes by hand.
// Everything optional also accepts null, since Go sends null for nil pointers.

import { z } from 'zod'
import { DISCORD_ALERT_KINDS, ORDER_STATUSES, RETAILERS } from './enums'

const str = (max: number) => z.string().max(max)
const opt = <T extends z.ZodType>(t: T) => t.nullish().transform((v) => v ?? undefined)
const optStr = (max: number) => opt(str(max).transform((s) => s.trim() || undefined))
const instant = z.iso.datetime({ offset: true })
const optMoney = opt(z.number().finite().min(0).max(10_000_000))
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

const address = z.object({
  recipientName: optStr(200),
  addressNumber: optStr(40),
  addressStreet: optStr(200),
  addressLine2: optStr(200),
  city: optStr(120),
  state: optStr(40),
  zip: optStr(20),
})

export const syncOrder = z.object({
  orderNumber: str(64).min(1),
  retailer: z.enum(RETAILERS),
  email: str(320),
  status: z.enum(ORDER_STATUSES),
  statusManual: z.boolean().default(false),
  shipped: z.boolean(),
  delivered: z.boolean(),
  orderDate: instant,
  updatedAt: instant,
  deliveryDate: optStr(160),
  deliveryStart: opt(day),
  deliveryEnd: opt(day),
  trackingNumber: optStr(80),
  carrier: optStr(40),
  cardLastFour: optStr(8),
  cardType: optStr(40),
  subtotal: optMoney,
  tax: optMoney,
  total: optMoney,
  sourceMailbox: optStr(320),
  address: opt(address),
  billing: opt(address),
  items: z
    .array(
      z.object({
        name: str(500).min(1),
        price: optMoney,
        quantity: z.number().int().min(1).max(100_000).default(1),
        sku: optStr(120),
        imageUrl: optStr(2000),
        productLink: optStr(2000),
      }),
    )
    .max(200)
    .default([]),
  statusHistory: z
    .array(
      z.object({
        status: z.enum(ORDER_STATUSES),
        trackingNumber: optStr(80),
        occurredAt: instant,
        manual: z.boolean().default(false),
      }),
    )
    .max(200)
    .default([]),
})
export type SyncOrder = z.output<typeof syncOrder>

export const syncAccount = z.object({
  email: str(320).min(1),
  retailer: z.enum(RETAILERS),
  emailCount: z.number().int().min(0).default(0),
  lastSubject: optStr(500),
  sourceMailbox: optStr(320),
  firstSeenAt: instant,
  lastSeenAt: instant,
})
export type SyncAccount = z.output<typeof syncAccount>

export const syncAlert = z.object({
  /** Discord message id. */
  externalId: str(40).min(1),
  postedAt: instant,
  source: str(40),
  botLabel: str(120).default(''),
  kind: z.enum(DISCORD_ALERT_KINDS),
  reason: str(300).default(''),
  /** '' when the bot named a site we don't track. */
  retailer: opt(z.union([z.enum(RETAILERS), z.literal('').transform(() => undefined)])),
  site: str(120).default(''),
  orderNumber: str(64).default(''),
  quantity: z.number().int().min(0).max(100_000).default(0),
  profile: str(200).default(''),
  product: str(500).default(''),
  productUrl: str(2000).default(''),
  price: opt(z.number().finite().min(0).max(10_000_000)),
  email: str(320).default(''),
})
export type SyncAlert = z.output<typeof syncAlert>

/** Batch caps: keep one push well under the request-size and SQL-parameter limits. */
export const SYNC_LIMITS = { orders: 500, accounts: 1000, alerts: 500 } as const

/**
 * The request envelope. Rows are validated one by one afterwards (see
 * `parseSyncRows`), so a single bad row is reported back instead of failing
 * the whole batch.
 */
const rows = (max: number) => z.array(z.unknown()).max(max).default([])
/**
 * Email accounts were called "aliases" on the wire until 2026-10-08. A desktop
 * that hasn't updated yet still sends that key, so both are read; `accounts`
 * wins when both are present.
 */
const LEGACY_ALIASES = 'Deprecated: send these rows as `accounts`'
export const syncPush = z
  .object({
    orders: rows(SYNC_LIMITS.orders),
    accounts: rows(SYNC_LIMITS.accounts),
    aliases: rows(SYNC_LIMITS.accounts).describe(LEGACY_ALIASES),
    alerts: rows(SYNC_LIMITS.alerts),
  })
  .transform(({ aliases, accounts, ...rest }) => ({ ...rest, accounts: accounts.length ? accounts : aliases }))

/** The rows of a push that passed validation. */
export interface SyncRows {
  orders: SyncOrder[]
  accounts: SyncAccount[]
  alerts: SyncAlert[]
}

/** A row the cloud refused, so the desktop can log it and move on. */
export interface SyncRejected {
  kind: 'order' | 'account' | 'alert'
  /** Position in the request's array. */
  index: number
  /** Best-effort identifier, e.g. "target 1020…" or the account's email address. */
  key: string
  message: string
}

const field = (row: unknown, name: string) => {
  const v = (row as Record<string, unknown> | null)?.[name]
  return typeof v === 'string' ? v.slice(0, 120) : ''
}

/** Split a push into valid rows and per-row rejections. */
export function parseSyncRows(push: z.output<typeof syncPush>): SyncRows & { rejected: SyncRejected[] } {
  const rejected: SyncRejected[] = []
  const take = <T extends z.ZodType>(kind: SyncRejected['kind'], schema: T, list: unknown[], key: (row: unknown) => string) => {
    const ok: z.output<T>[] = []
    list.forEach((row, index) => {
      const r = schema.safeParse(row)
      if (r.success) return void ok.push(r.data)
      const issue = r.error.issues[0]
      rejected.push({ kind, index, key: key(row), message: issue ? `${issue.path.join('.') || 'row'}: ${issue.message}` : 'invalid row' })
    })
    return ok
  }
  return {
    orders: take('order', syncOrder, push.orders, (r) => `${field(r, 'retailer')} ${field(r, 'orderNumber')}`.trim()),
    accounts: take('account', syncAccount, push.accounts, (r) => `${field(r, 'retailer')} ${field(r, 'email')}`.trim()),
    alerts: take('alert', syncAlert, push.alerts, (r) => field(r, 'externalId')),
    rejected,
  }
}

/** Most keys of each kind in one delete request. */
export const SYNC_DELETE_LIMIT = 1000
const keys = <T extends z.ZodType>(t: T) => z.array(t).max(SYNC_DELETE_LIMIT).default([])

/**
 * Rows the desktop deleted locally, by key. Retailer is free text on purpose:
 * a key the cloud doesn't have is ignored, never an error, so a delete can't
 * get stuck the way an invalid row could.
 */
const accountKeys = () => keys(z.object({ retailer: str(40), email: str(320).min(1) }))
export const syncDelete = z
  .object({
    orders: keys(z.object({ retailer: str(40), orderNumber: str(64).min(1) })),
    accounts: accountKeys(),
    aliases: accountKeys().describe(LEGACY_ALIASES),
    /** Discord message ids. */
    alerts: keys(str(40).min(1)),
  })
  .transform(({ aliases, accounts, ...rest }) => ({ ...rest, accounts: accounts.length ? accounts : aliases }))
export type SyncDelete = z.output<typeof syncDelete>

/** Rows actually removed. */
export interface SyncDeleteResult {
  orders: number
  accounts: number
  alerts: number
}

export interface SyncPushResult {
  /** Rows accepted and stored. */
  orders: number
  accounts: number
  alerts: number
  rejected: SyncRejected[]
}

/** What a desktop sees about its own connection. */
export interface SyncStatus {
  deviceId: string
  deviceName: string
  workspaceId: string
  workspaceName: string
  lastSyncAt?: string
  /** Rows this device has uploaded. */
  orders: number
  limits: typeof SYNC_LIMITS
}

export const registerDevice = z.object({
  name: str(80).min(1),
  platform: str(40).default(''),
  appVersion: str(40).default(''),
  /** Workspace to sync into; defaults to the session's active workspace. */
  organizationId: optStr(64),
})
